import { useEffect, useRef, useState } from "react";
import { Play, AlertTriangle } from "lucide-react";
import { SNAP_MIN, fmtTime, hexA, tint, shade } from "./utils";

/**
 * A single rendered time-entry block inside a DayColumn. Supports
 * click-to-edit, drag-to-move, and resize-from-top/bottom.
 *
 * `hourPx` is the current pixels-per-hour scale (defaults to the utils
 * DEFAULT_HOUR_PX). Passed down from WeekCalendar so zoom stays in sync.
 */
export default function EntryBlock({
  entry, color, clients, activity, tagMap, onClickEntry, onUpdate, onResume,
  hourPx,
}) {
  const start = new Date(entry.start);
  const end = new Date(entry.end);
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes();

  const [preview, setPreview] = useState(null);
  const stateRef = useRef(null);

  const top = ((preview?.startMin ?? startMin) / 60) * hourPx;
  const height = Math.max(
    20,
    (((preview?.endMin ?? endMin) - (preview?.startMin ?? startMin)) / 60) * hourPx,
  );

  useEffect(() => {
    const onMove = (ev) => {
      const s = stateRef.current;
      if (!s) return;
      const deltaY = ev.clientY - s.initY;
      const deltaMin = Math.round((deltaY / s.hourPx) * 60 / SNAP_MIN) * SNAP_MIN;
      if (Math.abs(deltaY) > 3) s.didDrag = true;
      let newStart = s.initStartMin;
      let newEnd = s.initEndMin;
      if (s.mode === "move") {
        newStart = s.initStartMin + deltaMin;
        newEnd = s.initEndMin + deltaMin;
        const duration = s.initEndMin - s.initStartMin;
        if (newStart < 0) { newStart = 0; newEnd = duration; }
        if (newEnd > 1440) { newEnd = 1440; newStart = 1440 - duration; }
      } else if (s.mode === "resize-top") {
        newStart = Math.max(0, Math.min(s.initEndMin - SNAP_MIN, s.initStartMin + deltaMin));
        newEnd = s.initEndMin;
      } else if (s.mode === "resize-bottom") {
        newEnd = Math.min(1440, Math.max(s.initStartMin + SNAP_MIN, s.initEndMin + deltaMin));
        newStart = s.initStartMin;
      }
      s.lastStartMin = newStart;
      s.lastEndMin = newEnd;
      setPreview({ startMin: newStart, endMin: newEnd });
    };
    const onUp = () => {
      const s = stateRef.current;
      if (!s) return;
      const didDrag = s.didDrag;
      const finalStart = s.lastStartMin ?? s.initStartMin;
      const finalEnd = s.lastEndMin ?? s.initEndMin;
      stateRef.current = null;
      setPreview(null);
      if (!didDrag) { onClickEntry(); return; }
      if (finalStart === s.initStartMin && finalEnd === s.initEndMin) return;
      const baseDate = new Date(entry.start);
      const startDt = new Date(baseDate); startDt.setHours(0, 0, 0, 0); startDt.setMinutes(finalStart);
      const endDt = new Date(baseDate); endDt.setHours(0, 0, 0, 0); endDt.setMinutes(finalEnd);
      onUpdate(entry.id, {
        description: entry.description,
        client_ids: Array.isArray(entry.client_ids) ? entry.client_ids : (entry.client_id ? [entry.client_id] : []),
        activity_id: entry.activity_id,
        tag_ids: entry.tag_ids || [],
        leave_type: entry.leave_type || null,
        start: startDt.toISOString(),
        end: endDt.toISOString(),
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [entry, onClickEntry, onUpdate]);

  const begin = (mode) => (ev) => {
    if (ev.button !== 0) return;
    ev.stopPropagation();
    ev.preventDefault();
    stateRef.current = {
      mode,
      initY: ev.clientY,
      initStartMin: startMin,
      initEndMin: endMin,
      didDrag: false,
      hourPx,
    };
  };

  const displayStart = new Date(entry.start); displayStart.setHours(0, 0, 0, 0); displayStart.setMinutes(preview?.startMin ?? startMin);
  const displayEnd = new Date(entry.start); displayEnd.setHours(0, 0, 0, 0); displayEnd.setMinutes(preview?.endMin ?? endMin);

  const isLeave = !!entry.leave_type;
  // Only the 9 PM auto-stop warrants a highlight — overlap auto-stops are
  // expected/silent (the timer stopped because the user's own scheduled item
  // took over) so no visual noise.
  const showAutoStopWarning = entry.auto_stopped_reason === "past_9pm";
  const autoStopMsg = showAutoStopWarning
    ? "Auto-stopped at 9 PM — looked like you forgot to stop the timer. Edit the finish time if needed."
    : "";
  const baseBg = preview ? tint(color, 0.55) : tint(color, 0.78);
  const stripeOverlay = isLeave
    ? `repeating-linear-gradient(135deg, ${hexA(color, 0.32)} 0 6px, transparent 6px 12px)`
    : null;

  return (
    <div
      onMouseDown={begin("move")}
      className={`entry-block group absolute left-1 right-1 rounded-md px-2 py-1 text-xs overflow-hidden z-20 border-l-4 select-none ${
        showAutoStopWarning ? "ring-2 ring-amber-400 ring-offset-1" : ""
      }`}
      style={{
        top,
        height,
        background: stripeOverlay ? `${stripeOverlay}, ${baseBg}` : baseBg,
        borderLeftColor: color,
        color: shade(color, 0.35),
        cursor: preview ? "grabbing" : "grab",
        boxShadow: preview ? "0 8px 20px -8px rgba(15,23,42,0.25)" : undefined,
      }}
      data-testid={`time-entry-${entry.id}`}
      data-leave={isLeave ? "true" : "false"}
      data-auto-stopped={entry.auto_stopped_reason || undefined}
      title={autoStopMsg || undefined}
    >
      <div onMouseDown={begin("resize-top")}
           className="absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-slate-900/10"
           data-testid={`time-entry-resize-top-${entry.id}`} />
      <div onMouseDown={begin("resize-bottom")}
           className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-slate-900/10"
           data-testid={`time-entry-resize-bottom-${entry.id}`} />

      <div className="font-semibold truncate pointer-events-none">
        {showAutoStopWarning && (
          <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5 text-amber-600"
                         data-testid={`time-entry-autostop-icon-${entry.id}`} />
        )}
        {entry.leave_type ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-[9px] uppercase tracking-widest opacity-70">
              {entry.leave_type === "annual" ? "Annual Leave" :
               entry.leave_type === "personal" ? "Personal Leave" : "Sick Leave"}
            </span>
          </span>
        ) : (entry.description || "(no description)")}
      </div>
      {height > 32 && (
        <div className="flex items-center gap-1.5 text-[10px] mt-0.5 opacity-80 pointer-events-none">
          {clients && clients.length > 0 && (
            <span className="truncate">
              {clients.length === 1 ? clients[0].name : `${clients[0].name} +${clients.length - 1}`}
            </span>
          )}
          {clients && clients.length > 0 && activity && <span>·</span>}
          {activity && <span className="truncate">{activity.name}</span>}
        </div>
      )}
      {height > 48 && Array.isArray(entry.tag_ids) && entry.tag_ids.length > 0 && (
        <div className="flex items-center gap-1 mt-1 flex-wrap pointer-events-none">
          {entry.tag_ids.slice(0, 3).map((tid) => {
            const t = tagMap?.[tid];
            if (!t) return null;
            return (
              <span key={tid}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold"
                    style={{ background: `${t.color}33`, color: t.color }}
                    data-testid={`time-entry-tag-${entry.id}-${tid}`}>
                {t.name}
              </span>
            );
          })}
          {entry.tag_ids.length > 3 && (
            <span className="text-[9px] opacity-60">+{entry.tag_ids.length - 3}</span>
          )}
        </div>
      )}
      <div className="absolute bottom-1 right-1.5 timer-font text-[10px] opacity-70 pointer-events-none">
        {fmtTime(displayStart)} – {fmtTime(displayEnd)}
      </div>
      {!entry.leave_type && onResume && (
        <button
          onMouseDown={(ev) => ev.stopPropagation()}
          onClick={(ev) => { ev.stopPropagation(); onResume(entry); }}
          className="entry-resume-btn absolute top-1 right-1 h-5 w-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
          style={{ background: color, color: "white" }}
          title="Resume this entry"
          data-testid={`time-entry-resume-${entry.id}`}
        >
          <Play className="h-2.5 w-2.5 fill-current" />
        </button>
      )}
    </div>
  );
}
